// @flow

import _ from 'lodash';
import React, { Component, lazy } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import { Switch, Redirect as RedirectRoute, withRouter } from 'react-router-dom';
import queryString from 'query-string';
import history from 'src/routes/history';
import { routes } from 'src/routes';

// Import helper
import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';
import { initIntercom } from 'src/integrations/chatbox';
import { initVitally } from 'src/integrations/vitally';
import { initCustomerIO } from 'src/integrations/customerio';
import { initPosthog } from 'src/integrations/posthog';
import { setupFullStoryUser } from 'src/integrations/fullStory';

import { ShopifyEmbeddedAppRoute } from 'src/ShopifyEmbeddedAppRoute';
import { Empty } from 'src/components/views/common/empty';
import {
  checkUserV3,
  getSubscriptionStatus as getSubscriptionStatusV3,
  enableSubscriptionV4,
  isGMVMigrationStore
} from 'src/helpers/subscription-v3-helper';

import {
  getSubscriptionStatus,
  checkShopifyPaidPlan,
  isSpecialDiscount2021Expired
} from 'src/helpers/subscription-helper';

import * as oauthAction from 'actions/oauth-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import * as installActions from 'actions/install-actions';
import * as dashboardV3Actions from 'actions/dashboard-v3-actions';
import * as subscriptionV5Actions from 'src/stores/actions/subscription-v5-actions';
import * as commonAction from 'src/stores/actions/common-actions';
import * as themeIntegrationAction from 'src/stores/actions/shopify-integration/theme-integration-actions';

import {
  enableAnalyticV2,
  enableAnalyticV2Recommendation,
  enableAnalyticV2Settings,
  initAnalyticDataToStorage
} from 'src/helpers/analytics-v2-helpers';
import {
  enableThemeSetupV3,
  enableShopifyIntegration,
  isUsingNewTheme
} from 'src/helpers/theme-setup-helper';
import { enableOnboardingV3 } from 'src/helpers/installation-helper';
import { enableDashboardV4 } from 'src/helpers/dashboard-helper';
import { disableInstallStatusForDashboardV4 } from 'src/helpers/dashboard-helper';
import { buildAuthURL, getShop } from 'src/helpers';
import eventTracker from 'src/libs/event-tracker';
import { isInWhiteListTurboBanner } from 'src/helpers/whitelist';
import { getCurrentPlanStatus } from 'src/helpers/subscription-v5-helper';
import { shopifyRedirect } from 'src/integrations/shopify-app-bridge/redirect';

//Common
const LandingPageNewFeatures = lazy(
  () => import('src/components/containers/landing-page/new-features')
);
const LandingPageAIPowered = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/landing-page/ai-powered')
);
const LandingPageTurbo = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/landing-page/turbo')
);
//
const ChooseThemeScreen = lazy(
  () => import('src/components/containers/installation-v2/screen.choose-theme')
);
const RunningSyncScreen = lazy(
  () => import('src/components/containers/installation-v2/screen.running-sync')
);
const Onboarding = lazy(
  () => import('src/components/containers/installation-v2/screen.onboarding')
);
const OnboardingPlan = lazy(
  () => import('src/components/containers/installation-v2/screen.onboarding-plan')
);
const KYCScreen = lazy(() => import('src/components/containers/installation-v2/screen.kyc'));
const SurveyScreen = lazy(() => import('src/components/containers/installation-v2/screen.survey'));
const SuccessInstallScreen = lazy(
  () => import('src/components/containers/installation-v2/screen.success-install')
);
const SelectThemeSimplifyScreen = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/installation-v2/screen.select-theme-simplify'
    )
);

const ScreenOnboardingCompletion = lazy(
  () =>
    import(/* webpackPreload: true */ 'src/components/containers/installation-v2/screen.completion')
);
const SyncTrigger = lazy(() => import('src/components/containers/installation-v2/syncTrigger'));
const SyncNotification = lazy(() => import('src/components/containers/sync/sync-notification'));

// Setup
const Demographic = lazy(() => import('src/components/containers/installation/demographic'));
const LayoutSelection = lazy(
  () => import('src/components/containers/installation/1-select-layout')
);
const Initializing = lazy(() => import('src/components/containers/installation/2-initializing'));
const SetupComplete = lazy(() => import('src/components/containers/installation/3-setup-complete'));
const VerifySetup = lazy(() => import('src/components/containers/installation/4-verify-setup'));

// Onboarding
// Plan
const Plan = lazy(() => import('src/components/containers/subscription/plan'));
const PlanDiscount = lazy(() => import('src/components/containers/subscription/plan-discount'));
const PlanDiscountLarge = lazy(
  () => import('src/components/containers/subscription/plan-discount-large')
);
const Charge = lazy(() => import('src/components/containers/subscription/charge'));
const PlanComplete = lazy(() => import('src/components/containers/subscription/complete'));
const PlanError = lazy(() => import('src/components/containers/subscription/error'));
const PlanMigration = lazy(() => import('src/components/containers/subscription/migration'));
const PlanMigrationComplete = lazy(
  () => import('src/components/containers/subscription/migration/complete')
);
const ChargeCustomization = lazy(() => import('src/components/containers/charge-customization'));
const ChargeCustomizationComplete = lazy(
  () => import('src/components/containers/charge-customization/complete')
);
const ChargeCustomizationError = lazy(
  () => import('src/components/containers/charge-customization/error')
);

// PlanV3
const PlanV3 = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/subscription-v3/plan')
);
const ChargeV3 = lazy(() => import('src/components/containers/subscription-v3/charge'));
const PlanMigrationV3 = lazy(() => import('src/components/containers/subscription-v3/migration'));
const PlanMigrationCompleteV3 = lazy(
  () => import('src/components/containers/subscription/migration/complete')
);
const PlanPackage = lazy(() => import('src/components/containers/subscription-v3/package'));
const PackageComparison = lazy(
  () => import('src/components/containers/subscription-v3/package-comparison')
);
const PlanAddon = lazy(() => import('src/components/containers/subscription-v3/addon'));

// PlanV4
const PlanV4 = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/subscription-v4/plan')
);
const ChargeV4 = lazy(() => import('src/components/containers/subscription-v4/charge'));
const PlanMigrationV4 = lazy(() => import('src/components/containers/subscription-v4/migration'));
const PlanMigrationCompleteV4 = lazy(
  () => import('src/components/containers/subscription/migration/complete')
);
const PlanPackageV4 = lazy(() => import('src/components/containers/subscription-v4/package'));
const PackageComparisonV4 = lazy(
  () => import('src/components/containers/subscription-v4/package-comparison')
);
const PlanAddonV4 = lazy(() => import('src/components/containers/subscription-v4/addon'));

// PlanV5
const PlanV5 = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/subscription-v5/plan/index')
);
const ChargeV5 = lazy(() => import('src/components/containers/subscription-v5/charge'));
const PlanMigrationV5 = lazy(
  () => import('src/components/containers/subscription-v5/migration/index')
);
const PlanMigrationCompleteV5 = lazy(
  () => import('src/components/containers/subscription-v5/migration/complete')
);
const PlanCompleteV5 = lazy(() => import('src/components/containers/subscription-v5/complete'));

const PackageComparisonV5 = lazy(
  () => import('src/components/containers/subscription-v5/package-comparison')
);

const UpgradePackageV5 = lazy(() => import('src/components/containers/subscription-v5/package'));

const GMVBillingHistory = lazy(
  () => import('src/components/containers/subscription-v5/gmv-billing-history')
);

// Subscription V5 Migration From Old
const PlanSubscriptionV5Migration = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/subscription-gmv-migration/plan/index'
    )
);

// Home
const Dashboard = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/dashboard/index')
);
const DashboardV3 = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/dashboard-v3/index')
);
const DashboardV4 = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/dashboard-v4/index')
);
// Sync
const SyncOverview = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/sync/index')
);
const SyncSetting = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/sync/sync-setting')
);
// Filter
const FilterTreeItem = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/filter-tree/item')
);
const ManageFilterTree = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/filter-tree/management')
);
const FilterSettings = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/filter-settings')
);
const FilterLayout = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/filter-layout')
);
const MergeValues = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/merge-values')
);
const CustomRangeSlider = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/custom-range-slider')
);
const SwatchSettings = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/swatch-settings')
);
const GeneralSwatch = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/general-swatch')
);
const GeneralSwatchSettings = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/general-swatch-settings')
);

// Theme
const ThemeSetup = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/theme-setup')
);
const ThemeSetupV3 = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/theme-setup-v3')
);
const ThemeUpgrade = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/theme-upgrade')
);
const ThemeUpgradeSuccess = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/theme-upgrade/success')
);
const VerifyUpgrade = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/theme-upgrade/verify-upgrade')
);
const SupportedThemeSetupSuccess = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/theme-setup/supported-theme-setup-success'
    )
);
const UnsupportedThemeSetupSuccess = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/theme-setup/unsupported-theme-setup-success'
    )
);
const SwitchLayout = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/switch-layout')
);
const SwitchLayoutSuccess = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/switch-layout/switch-layout-success'
    )
);
const QuickViewCartSettings = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/quick-view-cart-settings')
);
const DefaultLayoutSettings = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/default-layout-settings')
);
const DefaultThemeSettings = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/default-theme-settings')
);
const ThemeSettingVisualEditor = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/theme-settings/visual-editor')
);
const ShopifyIntegrationSettingsVisualEditor = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/shopify-integration-settings/visual-editor'
    )
);

// Shopify Integration
const ThirdPartyIntegration = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/third-party-integration')
);
const ShopifyIntegration = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/shopify-integration')
);
const PreviewBoostTemplate = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/shopify-integration/preview-boost-template'
    )
);
const CodeEditor = lazy(
  () =>
    import(/* webpackPrefetch: true */ 'src/components/containers/shopify-integration/code-editor')
);
const ManageTemplateInstance = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/shopify-integration/manage-template-instance'
    )
);
const ShopifyIntegrationGuide = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/shopify-integration/guide')
);

// Pre order
const PreOrderManagement = lazy(
  () =>
    import(/* webpackPrefetch: true */ 'src/components/containers/pre-order/campaign/management')
);
const PreOrderView = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/pre-order/campaign/item')
);
const PreOrderNotificationSettings = lazy(
  () =>
    import(/* webpackPrefetch: true */ 'src/components/containers/pre-order/notification-settings')
);

// Domain settings
const DomainSettings = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/domain-settings')
);

// Back in stock
const BackInStockSettings = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/back-in-stock/back-in-stock-settings'
    )
);
// Back in stock
const BackInStockSettingsGuide = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/back-in-stock/back-in-stock-settings/guide'
    )
);
const BackInStockSubscription = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/back-in-stock/back-in-stock-subscriptions'
    )
);

const AddNewSubmissions = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/back-in-stock/back-in-stock-subscriptions/submissions/new'
    )
);

// AB testing
const ABTestingCampaignManagement = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/ab-testing/management')
);
const ABTestingCampaignView = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/ab-testing/item/index')
);

const ABTestingCampaignAnalytic = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/ab-testing/item/analytics')
);

// Tool
const Translation = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/tools/translation')
);
const Integration = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/tools/integration')
);
const ItemMergeValues = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/merge-values/item')
);
const Metafield = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/tools/metafield')
);
const CustomSorting = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/tools/custom-sorting')
);
const VariantsDisplay = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/tools/variants-display')
);
const LanguageSetting = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/tools/language-setting')
);

// Account
const AccountDetails = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/account/account-details')
);
const AppPreferences = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/account/app-preferences')
);

// Search
const SearchControl = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/search-control')
);
const AdvancedSearch = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/advanced-search')
);
const StandardSearch = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/standard-search')
);
const TestYourSearch = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/test-your-search')
);
const ProductRanking = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/product-ranking')
);
const Merchandising = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/merchandising')
);
const MerchandisingRule = lazy(() => import('src/components/containers/merchandising/rule'));
const MerchandisingV2 = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/merchandising-v2')
);
const MerchandisingV3 = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/merchandising-v3')
);
const MerchandisingV2Rule = lazy(() => import('src/components/containers/merchandising-v2/rule'));
const ProductVisibility = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/product-visibility')
);
// Recommendation
const Widgets = lazy(
  () =>
    import(/* webpackPrefetch: true */ 'src/components/containers/recommendation/widget/management')
);
//Personalization
const Personalization = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/personalization')
);
const WidgetsStepGuide = lazy(
  () => import('src/components/containers/recommendation/widget/step-guide')
);
const WidgetsStepGuideCartDrawer = lazy(
  () => import('src/components/containers/recommendation/widget/step-guide/index-cart-drawer')
);

const WidgetItem = lazy(() => import('src/components/containers/recommendation/widget/item'));
const NewWidgetItem = lazy(
  () => import('src/components/containers/recommendation/widget/item/new')
);
const ManageManualRecommendation = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/recommendation/manual-recommendation/management'
    )
);
const ManualRecommendationItem = lazy(
  () => import('src/components/containers/recommendation/manual-recommendation/item')
);
const PredictiveBundle = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/recommendation/predictive-bundle/management'
    )
);
const PredictiveBundleItem = lazy(
  () => import('src/components/containers/recommendation/predictive-bundle/item')
);
const RecommendationDefaultDesignSettings = lazy(
  () => import('src/components/containers/recommendation/default-design-settings')
);
const RecommendationGeneralSettings = lazy(
  () =>
    import(/* webpackPrefetch: true */ 'src/components/containers/recommendation/general-settings')
);

// Analytics
const Analytics = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/analytics')
);

// Analytics Version 2
const SearchAnalytics = lazy(
  () =>
    import(/* webpackPrefetch: true */ 'src/components/containers/analytics-v2/search-analytics')
);
const SearchAnalyticsSearchTermDetail = lazy(
  () =>
    import('src/components/containers/analytics-v2/search-analytics/searches/search-term-detail')
);
const SearchAnalyticsSearchTermWithClickDetail = lazy(
  () =>
    import('src/components/containers/analytics-v2/search-analytics/searches-with-click/search-term-detail')
);
const AppImpact = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/analytics-v2/app-impact')
);
const CollectionAnalytics = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/analytics-v2/collection-analytics'
    )
);
const RecommendationAnalytics = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ 'src/components/containers/analytics-v2/recommendation-analytics'
    )
);
const PreOrderAnalytics = lazy(
  () =>
    import(/* webpackPrefetch: true */ 'src/components/containers/analytics-v2/pre-order-analytics')
);
const AnalyticSettings = lazy(
  () => import(/* webpackPrefetch: true */ 'src/components/containers/analytics-v2/settings')
);

const ComingExpiredModal = lazy(
  () => import('src/components/views/subscription/coming-expired-modal')
);

const BFCMOfferModal = lazy(() => import('src/components/views/common/BFCM-2021-offer-modal'));

const InstallStatusPopup = lazy(() => import('src/components/views/install-status-popup'));

const SearchPersonalizationPresentModal = lazy(
  () => import('src/components/views/common/search-personalization-present-modal')
);

// Update permission success
const UpdatePermissionModal = lazy(
  () => import('src/components/containers/app-permission/update-permission-modal')
);
const UpdatePermissionSuccess = lazy(
  () => import('src/components/containers/app-permission/success')
);
const AnalyticsThemeV1PermissionSuccess = lazy(
  () => import('src/components/containers/app-permission/analytics-theme-v1-success')
);

const KlaviyoIntegration = lazy(() => import('src/components/containers/klaviyo'));
const KlaviyoIntegrationSuccess = lazy(
  () => import('src/components/containers/klaviyo/callback-success')
);

const ReviewsIoIntegration = lazy(() => import('src/components/containers/reviews-io'));
const AttentiveIntegration = lazy(() => import('src/components/containers/attentive'));
const AttentiveIntegrationSuccess = lazy(
  () => import('src/components/containers/attentive/callback-success')
);
const GorgiasIntegration = lazy(() => import('src/components/containers/gorgias'));
const ShopifyCombinedListingsIntegration = lazy(
  () => import('src/components/containers/shopify-combined-listings')
);

const Settings = lazy(() => import('src/components/containers/settings'));
// B2B
const WholesaleSelling = lazy(() => import('src/components/containers/b2b/wholesale-selling'));
const GlobalSelling = lazy(() => import('src/components/containers/b2b/global-selling'));

// Post Purchase
const PostPurchase = lazy(() => import('src/components/containers/post-purchase'));
const PostPurchaseNew = lazy(() => import('src/components/containers/post-purchase/new'));
const PostPurchaseEdit = lazy(() => import('src/components/containers/post-purchase/edit'));

// Countdown
const Countdown = lazy(() => import('src/components/containers/countdown'));
const CountdownNew = lazy(() => import('src/components/containers/countdown/visual-editor/new'));
const CountdownEdit = lazy(() => import('src/components/containers/countdown/visual-editor/edit'));

// const VISIT_TIME = 5 * 60 * 1000;
const INTERVAL_QUERY = 5000;

const GroupStandardSearchRoute = [
  // Relevance
  {
    route: routes.relevanceSettings.view(),
    label: 'Standard search relevance settings'
  },
  // ISW
  {
    route: routes.instantSearchWidget.view(),
    label: 'Standard search ISW'
  },
  // Search result page
  {
    route: routes.searchResultsPage.view(),
    label: 'Standard search Search result page'
  },
  // In collection search
  {
    route: routes.inCollectionSearch.view(),
    label: 'Standard search In-collection search'
  },
  // Synonym
  {
    route: routes.synonym.view(),
    label: 'Standard search synonym'
  },
  {
    route: routes.synonym.new(),
    label: 'Standard search new Synonym'
  },
  {
    route: routes.synonym.edit(),
    label: 'Standard search edit Synonym'
  },
  // Suggestion dictionary
  {
    route: routes.suggestionDictionary.view(),
    label: 'Standard search SuggestionDictionary'
  },
  {
    route: routes.suggestionDictionary.new(),
    label: 'Standard search new SuggestionDictionary'
  },
  {
    route: routes.suggestionDictionary.edit(),
    label: 'Standard search edit SuggestionDictionary'
  },
  // Redirect
  {
    route: routes.redirect.view(),
    label: 'Standard search redirect'
  },
  {
    route: routes.redirect.new(),
    label: 'Standard search new redirect'
  },
  {
    route: routes.redirect.edit(),
    label: 'Standard search edit redirect'
  },
  // Stop words
  {
    route: routes.stopWords.view(),
    label: 'Standard search Stop words'
  }
];

const GroupNewInstallSetupThemeOnboardingRoute = [
  {
    route: routes.newInstallation.selectFeature(),
    label: 'Onboarding - Select features'
  },
  {
    route: routes.newInstallation.selectThemeNewSimplify(),
    label: 'Onboarding - Select theme'
  },
  {
    route: routes.newInstallation.selectThemeNewSimplifyRunning(),
    label: 'Onboarding - Select theme running sync'
  },
  {
    route: routes.newInstallation.selectTemplate(),
    label: 'Onboarding - Select template'
  },
  {
    route: routes.newInstallation.enableBoostCore(),
    label: 'Onboarding - Enable Boost Core'
  }
];

const GroupAdvancedSearchRoute = [
  // General
  {
    route: routes.generalAdvancedSearch.view(),
    label: 'Advanced search - general'
  },
  // AI Synonym
  {
    route: routes.aiSynonym.view(),
    label: 'Advanced search AI Synonym'
  },
  // Semantic search
  {
    route: routes.semanticSearch.view(),
    label: 'Advanced search Semantic search'
  },
  // Product performance ranking
  {
    route: routes.productPerformanceRanking.view(),
    label: 'Advanced search product performance ranking'
  },
  // Product performance ranking
  {
    route: routes.searchPersonalization.view(),
    label: 'Personalization'
  }
];

const GroupShopifyIntegrationRoute = [
  {
    route: routes.shopifyIntegration.view(),
    label: 'Shopify Integration'
  },
  {
    route: routes.shopifyIntegration.viewThemeWidget(':themeId'),
    label: 'Shopify Integration Edit'
  },
  {
    route: routes.shopifyIntegration.newThemeWidget(':themeId'),
    label: 'Shopify Integration New'
  },
  {
    route: routes.newInstallation.viewSetupTheme(':themeId'),
    label: 'Onboarding - Shopify Integration Edit'
  },
  {
    route: routes.newInstallation.setupTheme(':themeId'),
    label: 'Onboarding - Shopify Integration New'
  },
  {
    route: routes.shopifyIntegration.shopifyIntegrationNewSimplify(':themeId'),
    label: 'Shopify Integration New Flow Create'
  },
  {
    route: routes.shopifyIntegration.viewShopifyIntegrationSimplify(':themeId'),
    label: 'Shopify Integration New Flow'
  },
  {
    route: routes.shopifyIntegration.upgradeToTurbo(':themeId'),
    label: 'Shopify Integration Upgrade To Turbo'
  }
];

class Main extends Component {
  queryInstallStatusInterval = undefined;
  unListen = undefined;
  constructor(props) {
    super(props);
    this.timeoutId = null;
  }

  state = {
    queries: {},
    showedNotification: false,
    subscriptionStatus: '',
    dayLeft: 0,
    showInstallationPopup: false
  };

  componentWillUnmount() {
    if (this.unListen !== undefined) this.unListen();
  }

  componentDidMount() {
    // window.addEventListener('beforeunload', this.handleBeforeLeave);

    this.setState({ pathname: this.props.location.pathname });
    const {
      connectShopify,
      connected,
      subscriptionV3,
      subscriptionV4,
      isOnboardingV3,
      showInstallationSuccessBanner,
      isNewUser,
      getInstallStatus,
      getGMVMigrationStores,
      updateQueryParams,
      i18n,
      authToken,
      checkLiveThemeTurbo
    } = this.props;
    if (this.props.location.search) {
      updateQueryParams(this.props.location.search);
    }

    if (connected) {
      const { tenantApp = {} } = this.props;
      getGMVMigrationStores();

      // Set full story property
      setupFullStoryUser(this.props.tenantApp);
      // Set tenant app to event tracker
      eventTracker.setTenantApp(this.props.tenantApp);

      // Get subscription status
      const { status, dayLeft } =
        subscriptionV3 || subscriptionV4
          ? getSubscriptionStatusV3(tenantApp)
          : getSubscriptionStatus(tenantApp);
      this.setState({ subscriptionStatus: status, dayLeft });

      const isInWhiteListTurbo = isInWhiteListTurboBanner(tenantApp.tenantId);
      if (isInWhiteListTurbo) {
        checkLiveThemeTurbo();
      }
      // Init Intercom - Vitally NPS - GA4
      this.unListen = initIntercom(tenantApp, this.props.history);
      initVitally(tenantApp);
      initCustomerIO(tenantApp);
      initPosthog(tenantApp);
    } else if (!authToken || authToken === '') {
      const queries = queryString.parse(this.props.location.search);
      this.setState({ queries });
      connectShopify(queries).then(() => initAnalyticDataToStorage(i18n));
    }
    this.handleShowChatbox();

    const isEnableShopifyIntegration = enableShopifyIntegration(this.props.tenantApp);

    // get installation status
    if (isOnboardingV3 && showInstallationSuccessBanner && isNewUser && !isEnableShopifyIntegration)
      getInstallStatus();
  }

  /**
   * Track page view
   */
  componentDidUpdate(prevProps) {
    // Check if the route has changed
    if (this.props.location?.pathname !== prevProps.location?.pathname) {
      // const {  tenantApp } = this.props;
      // const { tenantId } = tenantApp;
      eventTracker.trackPageView(this.props.location?.pathname, prevProps.location?.pathname);
    }
  }

  componentWillReceiveProps(nextProps) {
    // detect if user visit another route more than 5 min then show chatbox
    if (this.props.location.pathname !== nextProps.location.pathname) {
      this.handleSetPreviousPage(this.props.location.pathname);
      if (this.timeoutId !== null) this.onLeaveRoute();
      this.handleShowChatbox();
    }
    const {
      tenantApp,
      isOnboardingV3,
      showInstallationSuccessBanner,
      syncStatus,
      themeInstallStatus,
      enableNewFeatureOnboarding,
      subscriptionV5,
      isNewUser
    } = nextProps;
    const pathNamePrev = this.props.location.pathname;
    const pathName = nextProps.location.pathname;

    const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);

    // get install status
    if (
      isOnboardingV3 &&
      showInstallationSuccessBanner &&
      isNewUser &&
      !isEnableShopifyIntegration
    ) {
      if (syncStatus === 'done' && themeInstallStatus === 'done') {
        clearInterval(this.queryInstallStatusInterval);
        this.setState({ showInstallationPopup: true });
      } else {
        if (!this.queryInstallStatusInterval) {
          this.props.getInstallStatus();
          this.queryInstallStatusInterval = setInterval(() => {
            this.props.getInstallStatus();
          }, INTERVAL_QUERY);
        }
      }
    }
    if (pathName === '/charge' && !this.state.showedNotification) {
      this.setState({ showedNotification: true });
    }

    if (
      (!this.props.connected && nextProps.connected && nextProps.success) ||
      pathNamePrev !== pathName ||
      this.props.installStatus !== nextProps.installStatus
    ) {
      const { tenantApp } = nextProps;

      // Get subscription status
      let { status, dayLeft } =
        nextProps.subscriptionV4 || nextProps.subscriptionV3
          ? getSubscriptionStatusV3(tenantApp)
          : getSubscriptionStatus(tenantApp);
      if (subscriptionV5) {
        const currentPlanStatusV5 = getCurrentPlanStatus(tenantApp);
        status = currentPlanStatusV5?.status || status;
      }

      this.setState({ subscriptionStatus: status, dayLeft });

      // Handle subscription status
      // This process don't allow to go to another pages
      if (this.handleSubscriptionStatus(nextProps, status)) return;

      // Handle migration plan
      // This process don't allow to go to another pages
      if (this.handleMigrationPlan(nextProps)) return;
    }

    // Redirect first time logged in successfully
    if (!this.props.connected && nextProps.connected && nextProps.success) {
      nextProps.getGMVMigrationStores();
      if (!nextProps.authToken) {
        // Tenant app is not found or uninstalled, redirect to install page
        const redirectURL = buildAuthURL(nextProps.tenantId);
        if (window.top === window.self) {
          // Not embedded — safe to redirect
          window.location.href = redirectURL;
        } else {
          // Error when not finishing oauth flow
          // const queries = queryString.parse(nextProps.location.search);
          // this.props.logApplicationConfiguration(queries);
          shopifyRedirect(redirectURL);
        }
      }

      const { status, tenantId, tenantApp } = nextProps;
      const { location } = this.props;

      // Set full story property
      setupFullStoryUser(tenantApp);
      // Set tenant app to event tracker
      eventTracker.setTenantApp(tenantApp);

      const isInWhiteListTurbo = isInWhiteListTurboBanner(tenantApp.tenantId);
      if (isInWhiteListTurbo) {
        nextProps?.checkLiveThemeTurbo();
      }

      switch (status) {
        case 'active': {
          const { installStatus, tenantApp } = nextProps;

          const isEnableOnboardingV3 = enableOnboardingV3(tenantApp);
          const isNewUser = enableShopifyIntegration(tenantApp);

          if (isEnableOnboardingV3) {
            // Check status and go to correct page
            // Don't check status 'theme-setup-error' because we can go any page
            if (pathName === routes.subscription.charge()) {
              console.log('break charge', pathName);
              break;
            }

            switch (installStatus) {
              case '':
                if (
                  [
                    routes.newInstallation.onboarding(),
                    routes.newInstallation.chooseTheme(),
                    routes.newInstallation.successInstall(),
                    routes.newInstallation.running(),
                    routes.newInstallation.kyc(),
                    routes.newInstallation.survey(),
                    routes.newInstallation.selectThemeNewSimplify(),
                    routes.newInstallation.selectFeature()
                  ].includes(location.pathname)
                ) {
                  history.push(routes.dashboard.view());
                }
                break;
              case 'accepted-permissions':
              case 'submitted-story-telling-1':
              case 'submitted-story-telling-2':
              case 'submitted-story-telling-3':
              case 'submitted-story-telling-4':
              case 'submitted-story-telling-5':
              case 'submitted-demographic': // For old customer not done old onboarding
              case 'selected-layout':
              case 'synced-data': {
                console.log('redirect to onboarding', location.pathname);
                if (location.pathname !== routes.newInstallation.onboarding()) {
                  history.push(routes.newInstallation.onboarding());
                }
                break;
              }
              case 'submitted-onboarding-plan': {
                if (location.pathname !== routes.newInstallation.onboardingPlan()) {
                  history.push(routes.newInstallation.onboardingPlan());
                }
                break;
              }
              case 'submitted-kyc':
                if (isNewUser) {
                  history.push(routes.newInstallation.successInstall());
                  break;
                }
              // eslint-disable-next-line
              case 'submitted-kyc-1':
              case 'submitted-kyc-2':
              case 'submitted-kyc-3': {
                if (location.pathname !== routes.newInstallation.chooseTheme()) {
                  history.push(routes.newInstallation.chooseTheme());
                }
                break;
              }
              case 'submitted-onboarding-simplify': {
                const excludedRoutes = [
                  routes.newInstallation.selectThemeNewSimplify(),
                  routes.newInstallation.selectTemplate()
                ];
                const allowedRoutes = GroupNewInstallSetupThemeOnboardingRoute.filter(
                  item => !excludedRoutes.includes(item.route)
                ).map(item => item.route);

                if (!allowedRoutes.includes(location.pathname)) {
                  history.push(routes.newInstallation.selectFeature());
                }
                break;
              }
              case 'submitted-choose-theme': {
                if (location.pathname !== routes.newInstallation.running()) {
                  history.push(routes.newInstallation.running());
                }
                break;
              }
              case 'submitted-onboarding': {
                if (location.pathname !== routes.newInstallation.kyc()) {
                  history.push(routes.newInstallation.kyc());
                }
                break;
              }
              case 'submitted-opensurvey': {
                if (location.pathname !== routes.newInstallation.survey()) {
                  history.push(routes.newInstallation.survey());
                }
                break;
              }
              default:
                // Here not direct to dashboard as history.push(routes.dashboard.view()) because
                // if route came from other -> redirect to dashboard is incorrect
                break;
            }
          } else {
            // Check status and go to correct page
            // New feature0423: Old customer flow - onboarding - Will apply for all store
            // if (!tenantId.includes('bc-dev-v2') && !tenantId.includes('bc-dev-v1')) {
            //   // Don't check status 'theme-setup-error' because we can go any page
            //   switch (installStatus) {
            //     case 'accepted-permissions': {
            //       if (location.pathname !== routes.demographic.view()) {
            //         history.push(routes.demographic.view());
            //       }
            //       break;
            //     }
            //     case 'submitted-demographic': {
            //       if (location.pathname !== routes.chooseLayout.view()) {
            //         history.push(routes.chooseLayout.view());
            //       }
            //       break;
            //     }
            //     case 'selected-layout': {
            //       if (location.pathname !== routes.initializing.view()) {
            //         history.push(routes.initializing.view());
            //       }
            //       break;
            //     }
            //     case 'synced-data': {
            //       if (location.pathname !== routes.setupComplete.view()) {
            //         history.push(routes.setupComplete.view());
            //       }
            //       break;
            //     }
            //     default:
            //       break;
            //   }
            // } else {
            switch (installStatus) {
              // For old customer not done old onboarding
              case 'accepted-permissions':
              case 'submitted-demographic':
              case 'selected-layout':
              case 'synced-data': {
                if (location.pathname !== routes.newInstallation.onboarding()) {
                  history.push(routes.newInstallation.onboarding());
                }
                break;
              }
              default: {
                enableNewFeatureOnboarding(true, 'finished-old-onboarding', 'skip-onboard');
                history.push(routes.dashboard.view());
              }
            }
            // }
          }

          break;
        }
        case 'disabled': {
          if (location.pathname !== routes.plan.view(tenantId)) {
            history.push(routes.plan.view(tenantId));
          }
          break;
        }
        default:
          break;
      }
      // Init Intercom - Vitally NPS - GA4
      this.unListen = initIntercom(tenantApp, this.props.history);
      initVitally(tenantApp);
      initCustomerIO(tenantApp);
      initPosthog(tenantApp);
      // initGA4(tenantApp);
    }

    if (nextProps.loading === true && this.props.loading === false) {
      startSABLoading();
    }

    if (nextProps.loading === false && this.props.loading === true) {
      stopSABLoading();
    }
  }

  handleBeforeLeave = event => {
    const e = event || window.event;
    e.preventDefault();

    if (this.state.showInstallationPopup) {
      this.props.setShowInstallationStatusBanner({ enable: false, type: 'successBanner' });
    }

    if (!this.queryInstallStatusInterval) {
      clearInterval(this.queryInstallStatusInterval);
    }
  };

  /**
   * detect if user visit another route more than 5 min then show chatbox
   */
  handleShowChatbox = () => {
    // Disable temporarily, requested by CS on 9 Mar 2023
    // this.timeoutId = setTimeout(() => {
    //   showChatbox();
    // }, VISIT_TIME);
  };

  /**
   * clear time out if user redirect to another route
   */
  onLeaveRoute = () => {
    clearTimeout(this.timeoutId);
  };

  // Disable/enable scroll in product tour
  disableBody = target => {
    disableBodyScroll(target);
  };
  enableBody = target => enableBodyScroll(target);

  render() {
    const {
      i18n,
      connected,
      success,
      authToken,
      tenantApp,
      useBoltOn,
      isOpenFullScreen,
      location: infoRequest,
      defaultThemeSettings,
      merchandisingV2 = false,
      subscriptionV3 = false,
      subscriptionV5,
      migrationStores,
      recommendation,
      installStatus,
      isOnboardingV3 = false
    } = this.props;
    const { showedNotification, subscriptionStatus, dayLeft } = this.state;
    const { hmac } = this.state.queries;
    const activeRecommendation = _.get(recommendation, 'status', 'inactive');
    const enableRecommendation = _.get(recommendation, 'settings.enable', false);

    const isEnableAnalyticV2 = enableAnalyticV2(tenantApp);
    const isEnableAnalyticV2Recommendation = enableAnalyticV2Recommendation(tenantApp);
    const isEnableAnalyticV2Settings = enableAnalyticV2Settings(tenantApp);
    const checkShowAnalyticV2 = isEnableAnalyticV2;
    const isEnableThemeSetupV3 = enableThemeSetupV3(tenantApp);
    const isEnableOnboardingV3 = enableOnboardingV3(tenantApp);
    const isEnableSubscriptionV4 = enableSubscriptionV4(tenantApp);
    const isEnableDashboardV4 = enableDashboardV4(tenantApp);
    const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);

    const usingNewTheme = isUsingNewTheme(tenantApp);

    const renderDashboard = (isEnableDashboardV4, p) =>
      isEnableDashboardV4 ? <DashboardV4 {...p} /> : <DashboardV3 {...p} />;

    const renderSubscriptionComponent = (
      subscriptionV5,
      isEnableSubscriptionV4,
      subscriptionV3,
      V5MigrationComponent,
      V5Component,
      V4Component,
      V3Component,
      V1Component,
      p
    ) => {
      if (isGMVMigrationStore(tenantApp, migrationStores)) {
        return <V5MigrationComponent {...p} />;
      }
      if (subscriptionV5) return <V5Component {...p} />;
      // force redirect to dashboard if user access plan page while using bolt-on and not pricing v5
      if (useBoltOn) history.push(routes.dashboard.view());
      if (isEnableSubscriptionV4) return <V4Component {...p} />;
      if (subscriptionV3) return <V3Component {...p} />;
      return <V1Component {...p} />;
    };

    const renderShopifyIntegration = (
      isEnableShopifyIntegration,
      isEnableThemeSetupV3,
      V4Component,
      V3Component,
      V1Component,
      p
    ) => {
      if (isEnableShopifyIntegration) return <V4Component {...p} />;
      if (isEnableThemeSetupV3) return <V3Component {...p} />;
      return <V1Component {...p} />;
    };

    return (
      <React.Fragment>
        {connected ? (
          success ? (
            !!authToken ? (
              <React.Fragment>
                <Switch>
                  {/* Common */}
                  <ShopifyEmbeddedAppRoute
                    path={routes.landingPage.view()}
                    pageName={'Landing page'}
                    render={p => <LandingPageNewFeatures {...p} />}
                    showSurvey={false}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.aiPowered.view()}
                    pageName={'AI landing page'}
                    render={p => <LandingPageAIPowered {...p} />}
                    showSurvey={false}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.landing.turbo()}
                    pageName={'Turbo landing page'}
                    render={p => <LandingPageTurbo {...p} />}
                    showSurvey={false}
                  />
                  {/* Setup: New onboarding flow */}
                  <ShopifyEmbeddedAppRoute
                    path={routes.newInstallation.onboarding()}
                    pageName={'Onboarding'}
                    render={p => <Onboarding {...p} />}
                    showSurvey={false}
                  />

                  {isEnableOnboardingV3 && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.newInstallation.kyc()}
                      pageName={'KYCScreen'}
                      render={p => <KYCScreen {...p} />}
                      showSurvey={false}
                    />
                  )}
                  {isEnableOnboardingV3 && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.newInstallation.chooseTheme()}
                      pageName={'ChooseThemeScreen'}
                      render={p => <ChooseThemeScreen {...p} />}
                      showSurvey={false}
                    />
                  )}
                  {isEnableOnboardingV3 && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.newInstallation.running()}
                      pageName={'RunningSyncScreen'}
                      render={p => <RunningSyncScreen {...p} />}
                      showSurvey={false}
                    />
                  )}
                  {isEnableOnboardingV3 && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.newInstallation.successInstall()}
                      pageName={'SuccessInstallScreen'}
                      render={p => <SuccessInstallScreen {...p} />}
                      showSurvey={false}
                    />
                  )}
                  {isEnableOnboardingV3 && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.newInstallation.survey()}
                      pageName={'SurveyScreen'}
                      render={p => <SurveyScreen {...p} />}
                      showSurvey={false}
                    />
                  )}
                  {isEnableOnboardingV3 && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.newInstallation.onboardingPlan()}
                      pageName={'Onboarding Plan Screen'}
                      render={p => <OnboardingPlan {...p} />}
                      showSurvey={false}
                    />
                  )}

                  {/* Setup */}
                  <ShopifyEmbeddedAppRoute
                    path={routes.demographic.view()}
                    pageName={'Demographic'}
                    render={p => <Demographic {...p} />}
                    showSurvey={false}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.chooseLayout.view()}
                    pageName={'Select layout'}
                    render={p => <LayoutSelection {...p} />}
                    showSurvey={false}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.initializing.view()}
                    pageName={'Initializing'}
                    render={p => <Initializing {...p} />}
                    showSurvey={false}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.setupComplete.view()}
                    pageName={'Setup complete'}
                    render={p => <SetupComplete {...p} />}
                    showSurvey={false}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.verifySetup.view()}
                    pageName={'Verify setup'}
                    render={p => <VerifySetup {...p} />}
                    showSurvey={false}
                  />

                  {/* Plan */}

                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.plan()}
                    pageName={'Plan'}
                    render={p =>
                      renderSubscriptionComponent(
                        subscriptionV5,
                        isEnableSubscriptionV4,
                        subscriptionV3,
                        PlanSubscriptionV5Migration,
                        PlanV5,
                        PlanV4,
                        PlanV3,
                        Plan,
                        p
                      )
                    }
                  />

                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.comparison(':products')}
                    pageName={'Package comparison'}
                    render={p =>
                      renderSubscriptionComponent(
                        subscriptionV5,
                        isEnableSubscriptionV4,
                        subscriptionV3,
                        PackageComparisonV4,
                        PackageComparisonV4,
                        PackageComparisonV4,
                        PackageComparison,
                        PackageComparison,
                        p
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.comparisonV5()}
                    pageName={'Package comparison V5'}
                    render={p =>
                      subscriptionV5 || isGMVMigrationStore(tenantApp, migrationStores) ? (
                        <PackageComparisonV5 {...p} />
                      ) : (
                        <RedirectRoute to={routes.subscription.plan()} />
                      )
                    }
                  />

                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.gmvBillingHistory()}
                    pageName={'Subscription V5 - GMV and Billing History'}
                    render={p =>
                      subscriptionV5 ? (
                        <GMVBillingHistory {...p} />
                      ) : (
                        <RedirectRoute to={routes.subscription.plan()} />
                      )
                    }
                  />
                  {/* Plan v2 */}
                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.discount()}
                    pageName={'Plan discount'}
                    render={p => <PlanDiscount {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.discountLarge(':planId')}
                    pageName={'Plan discount with large plan'}
                    render={p => <PlanDiscountLarge {...p} />}
                  />

                  {/* Not available for subscription V5 */}
                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.package()}
                    pageName={'Plan package'}
                    render={p =>
                      renderSubscriptionComponent(
                        subscriptionV5,
                        isEnableSubscriptionV4,
                        subscriptionV3,
                        PlanPackageV4,
                        PlanPackageV4,
                        PlanPackageV4,
                        PlanPackage,
                        PlanPackage,
                        p
                      )
                    }
                  />

                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.upgradePackage()}
                    pageName={'Plan upgrade package V5'}
                    render={p =>
                      subscriptionV5 && !useBoltOn ? (
                        <UpgradePackageV5 {...p} />
                      ) : (
                        <RedirectRoute to={routes.subscription.plan()} />
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.packageLarge(':planNumber')}
                    pageName={'Plan package large'}
                    render={p =>
                      renderSubscriptionComponent(
                        subscriptionV5,
                        isEnableSubscriptionV4,
                        subscriptionV3,
                        PlanPackageV4,
                        PlanPackageV4,
                        PlanPackageV4,
                        PlanPackage,
                        PlanPackage,
                        p
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.addon()}
                    pageName={'Plan addon'}
                    render={p =>
                      renderSubscriptionComponent(
                        subscriptionV5,
                        isEnableSubscriptionV4,
                        subscriptionV3,
                        PlanAddonV4,
                        PlanAddonV4,
                        PlanAddonV4,
                        PlanAddon,
                        PlanAddon,
                        p
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.charge()}
                    showSurvey={false}
                    pageName={'Charge'}
                    render={p =>
                      renderSubscriptionComponent(
                        subscriptionV5,
                        isEnableSubscriptionV4,
                        subscriptionV3,
                        ChargeV5,
                        ChargeV5,
                        ChargeV4,
                        ChargeV3,
                        Charge,
                        p
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.complete()}
                    pageName={'Plan complete'}
                    render={p =>
                      renderSubscriptionComponent(
                        subscriptionV5,
                        isEnableSubscriptionV4,
                        subscriptionV3,
                        PlanCompleteV5,
                        PlanCompleteV5,
                        PlanComplete,
                        PlanComplete,
                        PlanComplete,
                        p
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.error()}
                    pageName={'Plan error'}
                    render={p =>
                      renderSubscriptionComponent(
                        subscriptionV5,
                        isEnableSubscriptionV4,
                        subscriptionV3,
                        PlanError,
                        PlanError,
                        PlanError,
                        PlanError,
                        PlanError,
                        p
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.migration()}
                    pageName={'Plan migration'}
                    render={p =>
                      renderSubscriptionComponent(
                        subscriptionV5,
                        isEnableSubscriptionV4,
                        subscriptionV3,
                        PlanMigrationV5,
                        PlanMigrationV5,
                        PlanMigrationV4,
                        PlanMigrationV3,
                        PlanMigration,
                        p
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.subscription.migrationComplete()}
                    pageName={'Plan migration complete'}
                    render={p =>
                      renderSubscriptionComponent(
                        subscriptionV5,
                        isEnableSubscriptionV4,
                        subscriptionV3,
                        PlanMigrationCompleteV5,
                        PlanMigrationCompleteV5,
                        PlanMigrationCompleteV4,
                        PlanMigrationCompleteV3,
                        PlanMigrationComplete,
                        p
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.chargeCustomization.view()}
                    pageName={'Charge customization'}
                    render={p => <ChargeCustomization {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.chargeCustomization.complete()}
                    pageName={'Charge customization complete'}
                    render={p => <ChargeCustomizationComplete {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.chargeCustomization.error()}
                    pageName={'Charge customization error'}
                    render={p => <ChargeCustomizationError {...p} />}
                  />
                  {/* Home */}
                  <ShopifyEmbeddedAppRoute
                    path={routes.dashboard.view()}
                    pageName={'Dashboard'}
                    render={p =>
                      isOnboardingV3 ? (
                        renderDashboard(isEnableDashboardV4, p)
                      ) : (
                        <Dashboard {...p} />
                      )
                    }
                    exact
                  />
                  {/* <ShopifyEmbeddedAppRoute
                    path={routes.dashboard.viewV3()}
                    pageName={'Dashboard V3'}
                    render={p => renderDashboard(isEnableDashboardV4, p)}
                    exact
                  /> */}

                  {/* Sync */}
                  <ShopifyEmbeddedAppRoute
                    path={routes.sync.view()}
                    pageName={'Sync'}
                    render={p => <SyncOverview {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.sync.settings()}
                    pageName={'Sync setting'}
                    render={p => <SyncSetting {...p} />}
                  />

                  {/* Analytics */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.view()}
                    pageName={'Analytics'}
                    render={p => {
                      if (checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.appImpact()} />;
                      }
                      return <Analytics {...p} />;
                    }}
                  />

                  {/* Filter */}
                  <ShopifyEmbeddedAppRoute
                    path={routes.filterTree.new()}
                    pageName={'Create new filter tree'}
                    render={p => <FilterTreeItem {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.filterTree.view(':filterTreeId')}
                    pageName={'Edit filter tree'}
                    render={p => <FilterTreeItem {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.filterTree.list()}
                    pageName={'Manage filter trees'}
                    render={p => <ManageFilterTree {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.filterSettings.view()}
                    pageName={'Filter settings'}
                    render={p => <FilterSettings {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.mergeValues.view()}
                    pageName={'Merge values'}
                    render={p => <MergeValues {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.mergeValues.new()}
                    pageName={'New merge values'}
                    render={p => <ItemMergeValues {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.mergeValues.edit(':type')}
                    pageName={'Edit merge values'}
                    render={p => <ItemMergeValues {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.customRangeSlider.view()}
                    pageName={'Custom range slider'}
                    render={p => <CustomRangeSlider {...p} />}
                  />
                  {usingNewTheme && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.swatchSettings.view()}
                      pageName={'Swatch settings'}
                      render={p => <SwatchSettings {...p} />}
                    />
                  )}
                  {usingNewTheme && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.filterLayout.view()}
                      pageName={'Filter layout'}
                      render={p => <FilterLayout {...p} />}
                    />
                  )}
                  {usingNewTheme && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.generalSwatch.settings(':filterName')}
                      pageName={'General swatch settings'}
                      render={p => <GeneralSwatchSettings {...p} />}
                    />
                  )}
                  {usingNewTheme && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.generalSwatch.view()}
                      pageName={'General swatch'}
                      render={p => <GeneralSwatch {...p} />}
                    />
                  )}
                  {/* Theme */}
                  <ShopifyEmbeddedAppRoute
                    path={routes.themeSetup.view()}
                    pageName={'Theme setup'}
                    render={p =>
                      renderShopifyIntegration(
                        isEnableShopifyIntegration,
                        isEnableThemeSetupV3,
                        ShopifyIntegration,
                        ThemeSetupV3,
                        ThemeSetup,
                        p
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.supportedThemeSetupSuccess.view()}
                    pageName={'Supported theme setup success'}
                    render={p => <SupportedThemeSetupSuccess {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.unsupportedThemeSetupSuccess.view()}
                    pageName={'Unsupported theme setup success'}
                    render={p => <UnsupportedThemeSetupSuccess {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.switchLayout.view()}
                    pageName={'Switch layout'}
                    render={p => <SwitchLayout {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.switchLayoutSuccess.view()}
                    pageName={'Switch layout success'}
                    render={p => <SwitchLayoutSuccess {...p} />}
                  />
                  {usingNewTheme ? (
                    <ShopifyEmbeddedAppRoute
                      path={routes.quickViewCartSettings.view()}
                      pageName={'Quick view & Cart settings'}
                      render={p => <QuickViewCartSettings {...p} />}
                    />
                  ) : (
                    <ShopifyEmbeddedAppRoute
                      path={routes.defaultLayoutSettings.view()}
                      pageName={'Default layout settings'}
                      render={p => <DefaultLayoutSettings {...p} />}
                    />
                  )}
                  {defaultThemeSettings && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.defaultThemeSettings.view()}
                      pageName={'Default theme settings'}
                      render={p => <DefaultThemeSettings {...p} />}
                    />
                  )}
                  <ShopifyEmbeddedAppRoute
                    path={routes.themeUpgrade.view()}
                    pageName={'Theme Upgrade'}
                    render={p => <ThemeUpgrade {...p} />}
                    showSurvey={false}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.themeUpgrade.success()}
                    pageName={'Theme Upgrade Success'}
                    render={p => <ThemeUpgradeSuccess {...p} />}
                    showSurvey={false}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.themeUpgrade.verify()}
                    pageName={'Verify Theme Upgrade'}
                    render={p => <VerifyUpgrade {...p} />}
                    showSurvey={false}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.themeSetting.visualEditor(':themeId')}
                    pageName={'Theme Setting Visual Editor'}
                    render={p => <ThemeSettingVisualEditor {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.shopifyIntegrationSettings.visualEditor(':templateId')}
                    pageName={'Shopify Integration Settings Visual Editor'}
                    render={p => <ShopifyIntegrationSettingsVisualEditor {...p} />}
                  />

                  {/* Theme Integration with SSR */}
                  <ShopifyEmbeddedAppRoute
                    path={routes.shopifyIntegration.codeEditor(':templateId')}
                    pageName={'Shopify Integration Code Editor'}
                    render={p => <CodeEditor {...p} />}
                  />

                  <ShopifyEmbeddedAppRoute
                    path={routes.shopifyIntegration.previewBoostTemplate(':themeNameLib')}
                    pageName={'Shopify Integration Preview Boost Template'}
                    render={p => <PreviewBoostTemplate {...p} />}
                  />

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.shopifyIntegration.guide(':themeId', ':templateId', ':feature')}
                    pageName={'Shopify Integration Guide'}
                    render={p => <ShopifyIntegrationGuide {...p} />}
                  />

                  {/* <ShopifyEmbeddedAppRoute
                    path={routes.shopifyIntegration.view()}
                    pageName={'Shopify Integration'}
                    render={p => <ShopifyIntegration {...p} />}
                  /> */}
                  {GroupShopifyIntegrationRoute.map((item, index) => (
                    <ShopifyEmbeddedAppRoute
                      exact
                      key={index}
                      path={item.route}
                      pageName={item.label}
                      render={p =>
                        renderShopifyIntegration(
                          isEnableShopifyIntegration,
                          isEnableThemeSetupV3,
                          ShopifyIntegration,
                          ThemeSetupV3,
                          ThemeSetup,
                          p
                        )
                      }
                    />
                  ))}

                  <ShopifyEmbeddedAppRoute
                    path={routes.shopifyIntegration.manageTemplateInstance(':themeId')}
                    pageName={'Manage Template Instance'}
                    render={p => <ManageTemplateInstance {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.shopifyIntegration.templateLibrary()}
                    pageName={'Template Library'}
                    render={p => <ManageTemplateInstance {...p} isTemplateLibrary />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.integration.thirdParty.view()}
                    pageName={'Third party integration'}
                    render={p => <ThirdPartyIntegration {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.integration.klaviyo.view()}
                    pageName={'Klaviyo integration'}
                    render={p => <KlaviyoIntegration {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.integration.attentive.view()}
                    pageName={'Attentive integration'}
                    render={p => <AttentiveIntegration {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.integration.klaviyo.callbackSuccess()}
                    pageName={'Klaviyo integration Success'}
                    render={p => <KlaviyoIntegrationSuccess {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.integration.reviewsio.view()}
                    pageName={'Reviews.io integration'}
                    render={p => <ReviewsIoIntegration {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.integration.attentive.callbackSuccess()}
                    pageName={'Attentive integration Success'}
                    render={p => <AttentiveIntegrationSuccess {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.integration.gorgias.view()}
                    pageName={'Gorgias integration'}
                    render={p => <GorgiasIntegration {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.integration.shopifyCombinedListings.view()}
                    pageName={'Shopify Combined Listings integration'}
                    render={p => <ShopifyCombinedListingsIntegration {...p} />}
                  />

                  {/* Tool */}
                  <ShopifyEmbeddedAppRoute
                    path={routes.translation.view()}
                    pageName={'Translation'}
                    render={p => <Translation {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.integration.view()}
                    pageName={'Integration'}
                    render={p => <Integration {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.metafield.view()}
                    pageName={'Metafield'}
                    render={p => <Metafield {...p} />}
                  />
                  {usingNewTheme && (
                    <ShopifyEmbeddedAppRoute
                      path={routes.customSorting.view()}
                      pageName={'Custom sorting'}
                      render={p => <CustomSorting {...p} />}
                    />
                  )}
                  <ShopifyEmbeddedAppRoute
                    path={routes.variantsDisplay.view()}
                    pageName={'Variants Display'}
                    render={p => <VariantsDisplay {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.languageSetting.view()}
                    pageName={'Language setting'}
                    render={p => <LanguageSetting {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.account.view()}
                    pageName={'Account details'}
                    render={p => <AccountDetails {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.appPreferences.view()}
                    pageName={'App preferences'}
                    render={p => <AppPreferences {...p} />}
                  />

                  {/* Search engine control */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.searchControl.view()}
                    pageName={'Standard search'}
                    render={p => <SearchControl {...p} />}
                  />
                  {/* Search preview: Test your search */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.testYourSearch.view()}
                    pageName={'Test your search'}
                    render={p => <TestYourSearch {...p} />}
                  />
                  {/* Standard search */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.standardSearch.view()}
                    pageName={'Standard search'}
                    render={p => <StandardSearch {...p} />}
                  />
                  {GroupStandardSearchRoute.map((item, index) => (
                    <ShopifyEmbeddedAppRoute
                      exact
                      key={index}
                      path={item.route}
                      pageName={item.label}
                      render={p => <StandardSearch {...p} />}
                    />
                  ))}
                  {GroupNewInstallSetupThemeOnboardingRoute.map((item, index) => (
                    <ShopifyEmbeddedAppRoute
                      key={index}
                      path={item.route}
                      pageName={item.label}
                      render={p => <SelectThemeSimplifyScreen {...p} />}
                      showSurvey={false}
                    />
                  ))}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.newInstallation.complete()}
                    pageName={'Installation completion'}
                    render={p => <ScreenOnboardingCompletion {...p} />}
                    showSurvey={false}
                  />
                  {/* Advanced Search */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.advancedSearch.view()}
                    pageName={'Advanced search'}
                    render={p => <AdvancedSearch {...p} />}
                  />
                  {GroupAdvancedSearchRoute.map((item, index) => (
                    <ShopifyEmbeddedAppRoute
                      exact
                      key={index}
                      path={item.route}
                      pageName={item.label}
                      render={p => <AdvancedSearch {...p} />}
                    />
                  ))}

                  {/* Merchandising */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.productRanking.view()}
                    pageName={'Product ranking'}
                    render={p => {
                      /**
                       * redirect to new merchandising
                       */
                      if (merchandisingV2)
                        return <RedirectRoute to={routes.merchandising.view()} />;
                      return <ProductRanking {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.merchandising.view()}
                    pageName={'Merchandising'}
                    render={p => {
                      if (isEnableShopifyIntegration) {
                        return <RedirectRoute to={routes.merchandising.search()} />;
                      }
                      return merchandisingV2 ? (
                        <MerchandisingV2 {...p} />
                      ) : (
                        <Merchandising {...p} />
                      );
                    }}
                  />
                  {isEnableShopifyIntegration && (
                    <ShopifyEmbeddedAppRoute
                      exact
                      path={routes.merchandising.search()}
                      pageName={'Merchandising search rule'}
                      render={p => <MerchandisingV3 {...p} />}
                    />
                  )}
                  {isEnableShopifyIntegration && (
                    <ShopifyEmbeddedAppRoute
                      exact
                      path={routes.merchandising.collection()}
                      pageName={'Merchandising collection rule'}
                      render={p => <MerchandisingV3 {...p} />}
                    />
                  )}

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.merchandising.new(':ruleType')}
                    pageName={'New merchandising rule'}
                    render={p =>
                      merchandisingV2 ? (
                        <MerchandisingV2Rule {...p} />
                      ) : (
                        <MerchandisingRule {...p} />
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.merchandising.edit(':ruleId', ':ruleType')}
                    pageName={'Edit merchandising rule'}
                    render={p =>
                      merchandisingV2 ? (
                        <MerchandisingV2Rule {...p} />
                      ) : (
                        <MerchandisingRule {...p} />
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    path={routes.productVisibility.view()}
                    pageName={'Product visibility'}
                    render={p => {
                      /**
                       * redirect to new merchandising
                       */
                      if (merchandisingV2)
                        return <RedirectRoute to={routes.merchandising.view()} />;
                      return <ProductVisibility {...p} />;
                    }}
                  />

                  {/* Recommendation */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.recommendationWidgets.new()}
                    pageName={'Create new recommendation widget'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <NewWidgetItem {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.recommendationWidgets.newPersonalized()}
                    pageName={'Create new recommendation widget'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <NewWidgetItem {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.recommendationWidgets.view(':widgetId')}
                    pageName={'Edit recommendation widget'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <WidgetItem {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.recommendationWidgets.list()}
                    pageName={'Recommendation Widgets'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <Widgets {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.recommendationWidgets.guide()}
                    pageName={'Recommendation Widgets - Steps guide install to theme'}
                    render={p => <WidgetsStepGuide {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.recommendationWidgets.guideCartDrawer()}
                    pageName={'Recommendation Widgets - Steps guide install widget cart drawer'}
                    render={p => <WidgetsStepGuideCartDrawer {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.recommendationGeneralSettings.settings()}
                    pageName={'General settings'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <RecommendationGeneralSettings {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.manualRecommendation.new()}
                    pageName={'Create new manual recommendation'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <ManualRecommendationItem {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.manualRecommendation.view(':recommendationId')}
                    pageName={'Edit manual recommendation'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <ManualRecommendationItem {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.manualRecommendation.list()}
                    pageName={'Manual Recommendations'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <ManageManualRecommendation {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.predictiveBundle.new()}
                    pageName={'Create new predictive bundle'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <PredictiveBundleItem {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.predictiveBundle.view(':widgetId')}
                    pageName={'Edit predictive bundle'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <PredictiveBundleItem {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.predictiveBundle.list()}
                    pageName={'Predictive bundle management'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <PredictiveBundle {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.recommendationDefaultDesignSettings.view()}
                    pageName={'Recommendation Default Design Settings'}
                    render={p =>
                      activeRecommendation === 'active' && enableRecommendation ? (
                        <RecommendationDefaultDesignSettings {...p} />
                      ) : (
                        renderDashboard(isEnableDashboardV4, p)
                      )
                    }
                  />

                  {/* Personalization */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.personalization.view()}
                    pageName="Personalization"
                    render={p => <Personalization {...p} />}
                  />

                  {/* Analytics V2 */}
                  {/* Search Analytics */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.overviewSearch()}
                    pageName={'Search Analytics Overview'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <SearchAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.searches()}
                    pageName={'Search Analytics Searches'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <SearchAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.searchesWithClick()}
                    pageName={'Search Analytics Searches With Clicks'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <SearchAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.searchTermDetail(':searchTerm')}
                    pageName={'Search Analytics Searches Term Detail'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <SearchAnalyticsSearchTermDetail {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.searchTermWithClickDetail(':searchTerm')}
                    pageName={'Search Analytics Searches Term With Click Detail'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <SearchAnalyticsSearchTermWithClickDetail {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.searchesWithoutClicks()}
                    pageName={'Search Analytics Searches Without Clicks'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <SearchAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.searchesWithoutResults()}
                    pageName={'Search Analytics Searches Without Results'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <SearchAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.productPerformance()}
                    pageName={'Search Analytics Product Performance}'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <SearchAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.appImpact()}
                    pageName={'Analytics App Impact'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      if (checkShowAnalyticV2 && !isEnableAnalyticV2Recommendation) {
                        return <RedirectRoute to={routes.analytics.overviewSearch()} />;
                      }
                      return <AppImpact {...p} />;
                    }}
                  />

                  {/* Collection Analytics */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.overviewCollection()}
                    pageName={'Collection Analytics Overview'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <CollectionAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.collectionPerformance()}
                    pageName={'Collection Performance'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <CollectionAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.filterOptionValue()}
                    pageName={'Filter Option Value'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <CollectionAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.filterOptionValueCombination()}
                    pageName={'Filter Option Value Combination'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      return <CollectionAnalytics {...p} />;
                    }}
                  />

                  {/* Recommendation Analytics */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.recommendationOverview()}
                    pageName={'Recommendation Analytics Overview'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      if (checkShowAnalyticV2 && !isEnableAnalyticV2Recommendation) {
                        return <RedirectRoute to={routes.analytics.appImpact()} />;
                      }
                      return <RecommendationAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.recommendationPagePerformance()}
                    pageName={'Recommendation Page Performance'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      if (checkShowAnalyticV2 && !isEnableAnalyticV2Recommendation) {
                        return <RedirectRoute to={routes.analytics.appImpact()} />;
                      }
                      return <RecommendationAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.recommendationWidgetPerformance()}
                    pageName={'Recommendation Widget Performance'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      if (checkShowAnalyticV2 && !isEnableAnalyticV2Recommendation) {
                        return <RedirectRoute to={routes.analytics.appImpact()} />;
                      }
                      return <RecommendationAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.recommendationProductPerformance()}
                    pageName={'Recommendation Product Performance'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      if (checkShowAnalyticV2 && !isEnableAnalyticV2Recommendation) {
                        return <RedirectRoute to={routes.analytics.appImpact()} />;
                      }
                      return <RecommendationAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.preOrders()}
                    pageName={'PreOrders'}
                    render={p => {
                      return <PreOrderAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.preOrderProduct()}
                    pageName={'PreOrder Product Analytics'}
                    render={p => {
                      return <PreOrderAnalytics {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.analytics.settings()}
                    pageName={'Analytic settings'}
                    render={p => {
                      if (!checkShowAnalyticV2) {
                        return <RedirectRoute to={routes.analytics.view()} />;
                      }
                      if (checkShowAnalyticV2 && !isEnableAnalyticV2Settings) {
                        return <RedirectRoute to={routes.analytics.appImpact()} />;
                      }
                      return <AnalyticSettings {...p} />;
                    }}
                  />
                  {/* Pre order */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.preOrder.list()}
                    pageName={'Pre order management'}
                    render={p => {
                      return <PreOrderManagement {...p} />;
                    }}
                  />

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.preOrder.new()}
                    pageName={'Pre order new'}
                    render={p => {
                      return <PreOrderView {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.preOrder.notifySetting()}
                    pageName={'Notification settings'}
                    render={p => {
                      return <PreOrderNotificationSettings {...p} />;
                    }}
                  />

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.preOrder.view(':campaignId')}
                    pageName={'Pre order edit'}
                    render={p => {
                      return <PreOrderView {...p} />;
                    }}
                  />
                  {/* Back in stock */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.backInStock.settings()}
                    pageName={'Back in stock - settings'}
                    render={p => {
                      return <BackInStockSettings {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.backInStock.guide()}
                    pageName={'Back in stock - settings guide'}
                    render={p => {
                      return <BackInStockSettingsGuide {...p} />;
                    }}
                  />

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.backInStock.subscription()}
                    pageName={'Back in stock - subscription'}
                    render={p => {
                      return <BackInStockSubscription {...p} />;
                    }}
                  />

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.backInStock.new()}
                    pageName={'Back in stock - add new subscriber'}
                    render={p => {
                      return <AddNewSubmissions {...p} />;
                    }}
                  />

                  {/* Revenue tools */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.tools.domainSetting()}
                    pageName={'Domain settings'}
                    render={p => {
                      return <DomainSettings {...p} />;
                    }}
                  />

                  {/* AB Testing */}

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.abTesting.list()}
                    pageName={'AB Testing - Campaign Management'}
                    render={p => {
                      return <ABTestingCampaignManagement {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.abTesting.new(':type')}
                    pageName={'AB Testing - Add new Campaign'}
                    render={p => {
                      return <ABTestingCampaignView {...p} />;
                    }}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.abTesting.view(':campaignId')}
                    pageName={'AB Testing - View and Edit Campaign'}
                    render={p => {
                      return <ABTestingCampaignView {...p} />;
                    }}
                  />

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.abTesting.analytics(':campaignId')}
                    pageName={'AB Testing - View Analytics Campaign'}
                    render={p => {
                      return <ABTestingCampaignAnalytic {...p} />;
                    }}
                  />

                  {/* Update permission success */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.appPermissionSuccess.view()}
                    pageName={'Update permission success'}
                    render={p => <UpdatePermissionSuccess {...p} />}
                  />

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.appPermissionSuccess.analyticsThemeV1()}
                    pageName={'Update analytics theme V1 permission success'}
                    render={p => <AnalyticsThemeV1PermissionSuccess {...p} />}
                  />

                  {/* Settings */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.settings.view()}
                    pageName={'Settings'}
                    render={p => <Settings {...p} />}
                  />

                  {/* B2B features */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.wholesaleSelling.view()}
                    pageName={'Wholesale selling'}
                    render={p => <WholesaleSelling {...p} />}
                  />

                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.globalSelling.view()}
                    pageName={'Global selling'}
                    render={p => <GlobalSelling {...p} />}
                  />

                  {/* Post Purchase */}
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.postPurchase.list()}
                    pageName={'Post Purchase Upsell'}
                    render={p => <PostPurchase {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.postPurchase.new()}
                    pageName={'Post Purchase Upsell New'}
                    render={p => <PostPurchaseNew {...p} />}
                  />
                  <ShopifyEmbeddedAppRoute
                    exact
                    path={routes.postPurchase.edit(':id')}
                    pageName={'Post Purchase Upsell Edit'}
                    render={p => <PostPurchaseEdit {...p} />}
                  />

                  {isEnableShopifyIntegration && (
                    <ShopifyEmbeddedAppRoute
                      exact
                      path={routes.countdown.list()}
                      pageName={'Countdown'}
                      render={p => <Countdown {...p} />}
                    />
                  )}
                  {isEnableShopifyIntegration && (
                    <ShopifyEmbeddedAppRoute
                      exact
                      path={routes.countdown.new()}
                      pageName={'Countdown new'}
                      render={p => <CountdownNew {...p} />}
                    />
                  )}
                  {isEnableShopifyIntegration && (
                    <ShopifyEmbeddedAppRoute
                      exact
                      path={routes.countdown.view(':countdownId')}
                      pageName={'Countdown edit'}
                      render={p => <CountdownEdit {...p} />}
                    />
                  )}

                  {/* Default */}
                  <ShopifyEmbeddedAppRoute
                    showSurvey={false}
                    render={p => renderDashboard(isEnableDashboardV4, p)}
                  />
                </Switch>
                {!isOpenFullScreen &&
                  (_.isEmpty(installStatus) ||
                    installStatus === 'old-customer-done-onboarding') && (
                    <>
                      <ComingExpiredModal
                        active={
                          subscriptionStatus === 'expiredSoon' &&
                          !showedNotification &&
                          isEnableOnboardingV3
                        }
                        dayLeft={dayLeft}
                        handleChangeModal={this.handleChangeModal}
                      />

                      <UpdatePermissionModal
                        pathName={this.props.location.pathname}
                        history={this.props.history}
                      />

                      {/* Affiliate Program */}
                      {/* <AffiliateProgramPresentModal tenantApp={tenantApp} /> */}
                      {/* Offer BFCM 2021 Modal */}
                      <BFCMOfferModal tenantApp={tenantApp} infoRequest={infoRequest} />

                      {/* Referral benefit Modal */}
                      {/* <ReferralBenefitModal tenantApp={tenantApp} /> */}

                      {/* Offer Password Protected Store*/}
                      {/* {<PasswordProtectedOfferModal tenantApp={tenantApp} infoRequest={infoRequest} />} */}

                      {/* Present Search personalization information */}
                      <SearchPersonalizationPresentModal tenantApp={tenantApp} />
                    </>
                  )}
                {/* Show installation status popup */}
                {this.allowShowInstallStatusPopup() && <InstallStatusPopup />}
                <SyncTrigger />
                <SyncNotification />
              </React.Fragment>
            ) : (
              ''
            )
          ) : hmac ? (
            <Empty message={i18n.translate('AdminHTML.OAuth.Empty.message')} />
          ) : (
            '' // Show blank page if hmac is missing
          )
        ) : (
          ''
        )}
      </React.Fragment>
    );
  }

  // Condition allow to show install status popup
  allowShowInstallStatusPopup = () => {
    const { isOpenFullScreen, isCompletedTour, tenantApp } = this.props;
    const { showInstallationPopup } = this.state;
    const isDisableInstallStatus = disableInstallStatusForDashboardV4(tenantApp);

    if (isDisableInstallStatus) return false;

    if (isOpenFullScreen) return false;
    if (this.props.location.pathname?.startsWith(routes.newInstallation.running())) return false;

    // not show popup when recommendation widget tour is open
    if (
      this.props.location.pathname?.startsWith(routes.recommendationWidgets.list()) &&
      isCompletedTour === false
    ) {
      return false;
    }
    if (showInstallationPopup) return true;
    return false;
  };

  // Handle subscription status
  handleSubscriptionStatus = (props, status) => {
    const { history, installStatus, planName, useBoltOn, tenantApp = {} } = props;
    const specialDiscountExpiredAt = _.get(tenantApp, 'specialDiscountExpiredAt', 0);
    const tenantId = _.get(tenantApp, 'tenantId', '');
    const passwordProtectedOfferExpired = _.get(
      tenantApp,
      'passwordProtectedOffer.isExpired',
      false
    );

    // Check dev stores
    const isPaymentPlan = checkShopifyPaidPlan(planName, tenantId);

    // Get charge_id from query params
    // Skip redirect to Plan page if status is expired or exceeded
    const queries = queryString.parse(props.location.search);
    const { charge_id } = queries;

    // Go to Subscription page if status is Expired, Exceeded
    const pathName = props.location.pathname;

    // Block access app after expired time
    const isSpecialDiscountExpired = isSpecialDiscount2021Expired(
      status,
      specialDiscountExpiredAt,
      7
    );

    // For exceeded store subscribe by bolt-on
    if (
      (_.isEmpty(installStatus) || installStatus === 'old-customer-done-onboarding') &&
      useBoltOn &&
      !charge_id &&
      !pathName.includes('/plan') &&
      !pathName.includes('/installation') &&
      status === 'exceeded'
    ) {
      history.push(routes.subscription.plan());

      return true;
    }

    // Block access app after regular plan expired
    if (
      (_.isEmpty(installStatus) || installStatus === 'old-customer-done-onboarding') &&
      ![
        '/plan',
        '/plan-discount',
        '/theme-setup',
        '/package',
        '/addon',
        '/gmv-billing-history'
      ].includes(pathName) &&
      !pathName.includes('/plan-discount-large/') &&
      !pathName.includes('/plan-large/') &&
      !pathName.includes('/package-comparison') &&
      !pathName.includes('/shopify-integration') &&
      !pathName.includes('/plan/enterprise') &&
      !pathName.includes('/installation') &&
      !useBoltOn &&
      !charge_id &&
      (status === 'expired' ||
        status === 'exceeded' ||
        status === 'regularExpired' ||
        isSpecialDiscountExpired ||
        passwordProtectedOfferExpired) &&
      isPaymentPlan
    ) {
      history.push(routes.subscription.plan());

      return true;
    }

    return false;
  };

  // Handle migration to es cluster
  handleMigrationPlan = props => {
    const { tenantApp, history } = props;

    // Get cluster migration
    const { elasticsearchClusterMigrationControl = {} } = tenantApp;

    // Check cluster migration
    if (!_.isEmpty(elasticsearchClusterMigrationControl)) {
      const { lastSyncAt = 0, lastSyncDoneAt = 0 } = elasticsearchClusterMigrationControl;
      const pathName = props.location.pathname;

      if (lastSyncDoneAt < lastSyncAt && !pathName.includes('/plan-migration')) {
        history.push(routes.subscription.migration());

        return true;
      }

      return false;
    }
  };

  //
  handleChangeModal = (label = '') => {
    const { trackEvent } = this.props;

    // Call to tracking event
    trackEvent('reminder', 'expiring', label ? label : 'close');
    this.setState({ showedNotification: true });
  };

  handleSetPreviousPage = page => {
    try {
      const shop = getShop();
      localStorage.setItem(`${shop}-previous-page`, page);
    } catch (error) {}
  };
}
const mapStateToProps = state => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const tenantId = _.get(state.oauth, 'tenantApp.tenantId', {});
  const useBoltOn = _.get(state.oauth, 'subscription.useBoltOn', false);
  const externalInfo = _.get(tenantApp, 'externalInfo', {});
  const isOpenFullScreen = _.get(state.appIntegration, 'openFullscreen', false);
  const recommendation = _.get(tenantApp, 'settings.recommendation', {});
  const syncStatus = _.get(state.install, 'syncStatus');
  const themeInstallStatus = _.get(state.install, 'installStatus');
  const recentInstalledAt = _.get(tenantApp, 'recentInstalledAt', 0);
  const showInstallationSuccessBanner = _.get(
    tenantApp,
    'settings.onboardingV3.settings.showInstallationSuccessBanner',
    false
  );

  return {
    loading: state.oauth.loading,
    connected: state.oauth.connected,
    success: state.oauth.connectSuccess,
    authToken: state.oauth.authToken,
    tenantId,
    tenantApp,
    useBoltOn,
    isOpenFullScreen,
    isNewUser: checkUserV3(recentInstalledAt),
    installStatus: _.get(tenantApp, 'installStatus'),
    planName: _.get(externalInfo, 'plan_name', ''),
    status: _.get(state.oauth.tenantApp, 'status', ''),
    merchandisingV2: _.get(tenantApp, 'settings.visualMerchandising.settings.enable', false),
    subscriptionV3: _.get(tenantApp, 'settings.subscriptionV3.settings.enable', true),
    subscriptionV4: _.get(tenantApp, 'settings.subscriptionV4.settings.enable', false),
    subscriptionV5: _.get(tenantApp, 'settings.subscriptionV5.settings.enable', false),
    migrationStores: _.get(state.subscriptionV5, 'migrationStores', []),
    isOnboardingV3: _.get(tenantApp, 'settings.onboardingV3.settings.enable', false),
    showInstallationSuccessBanner,
    defaultThemeSettings: _.get(tenantApp, 'adminAppFeatures.defaultThemeSettings', true), // Test purpose only
    recommendation,
    isCompletedTour: _.get(
      state.oauth.tenantApp,
      'settings.recommendation.settings.isCompletedTour',
      null
    ),
    syncStatus,
    themeInstallStatus
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  connectShopify: (query: Object) => dispatch(oauthAction.connectShopify(query, ownProps.i18n)),
  trackEvent: (eventSource, eventName, label) =>
    dispatch(checklistActions.trackEvent(eventSource, eventName, label)),
  enableNewFeatureOnboarding: (enable, source, action) =>
    dispatch(commonAction.enableNewFeatureOnboarding(enable, source, action, ownProps)),
  getInstallStatus: () => dispatch(installActions.getInstallStatus(ownProps.i18n)),
  setShowInstallationStatusBanner: enable =>
    dispatch(dashboardV3Actions.setShowInstallationStatusBanner(enable, ownProps.i18n)),
  getInvoiceV5: () => dispatch(subscriptionV5Actions.getDetailInvoice(ownProps.i18n)),
  getGMVMigrationStores: () => dispatch(subscriptionV5Actions.getGMVMigrationStores(ownProps.i18n)),
  updateQueryParams: (query: string) => dispatch(oauthAction.updateQueryParams(query)),
  checkLiveThemeTurbo: () => dispatch(themeIntegrationAction.checkLiveThemeTurbo(ownProps.i18n))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(withRouter(Main)));
